import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { forkJoin, of } from 'rxjs';
import { catchError, finalize, map, tap, withLatestFrom } from 'rxjs/operators';

import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { LinkedProductWithLinkable } from 'src/app/models/linked-product';
import { Product } from 'src/app/models/product';
import { ConfigService } from '../../../services/config.service';
import { HireItemService } from '../../../services/hire-item.service';
import { LinkedProductsService } from '../../../services/linked-products.service';
import { validateFieldIsUniqueInFormArray } from 'src/utils/validators/validateFieldIsUniqueInFormArray';

interface ItemForProduct {
  itemId: number;
  itemCode: string;
  available: boolean;
  deleted: boolean;
  hasCurrentWorkshopJobs: boolean;
}

@Component({
  selector: 'app-virtual-product-dialog',
  templateUrl: './virtual-product-dialog.component.html',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatDialogModule
  ],
  providers: [LinkedProductsService, HireItemService]
})
export class VirtualProductDialogComponent implements OnInit {
  loading = false;
  linkedProducts: LinkedProductWithLinkable<Product>[] = [];
  form: FormGroup;
  error: string | null = null;
  docketId: number;

  availableItemsForProducts: Map<number, ItemForProduct[]> = new Map();
  linkedProductsWithIndices: {
    linkedProduct: LinkedProductWithLinkable<Product>;
    index: number;
  }[] = [];

  constructor(
    private dialogRef: MatDialogRef<VirtualProductDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      product: Product;
      docketId?: number;
      estimatedOffHireDate?: Date;
    },
    private linkedProductsService: LinkedProductsService,
    private hireItemService: HireItemService,
    private configService: ConfigService,
    private fb: FormBuilder
  ) {
    this.docketId = data.docketId;
    this.form = this.fb.group({
      product_id: [data.product.id],
      on_hire: [new Date(), Validators.required],
      off_hire: [null],
      estimated_off_hire: [data.estimatedOffHireDate || null],
      location_out: [null, Validators.required],
      charge: [0, [Validators.min(0)]],
      selected_items: this.fb.array([])
    });

    this.form.valueChanges.subscribe((value) => {
      console.log({ value });
    });

    this.form.statusChanges.subscribe((status) => {
      console.log({ status });
    });
  }

  ngOnInit(): void {
    this.loading = true;

    this.linkedProductsService
      .getLinkedProductsAndTheirItems(this.data.product.id)
      .pipe(
        withLatestFrom(
          this.configService.currentLocation.pipe(
            map((location) => location.id)
          )
        ),
        tap(([linkedProducts, locationId]) => {
          this.linkedProducts = linkedProducts;
          this.form.patchValue({ location_out: locationId });
          this.initializeSelectedItems();
          this.initializeAvailableItemsForProducts();
          this.initializeLinkedProductsWithIndices();
        }),
        catchError((error) => {
          console.log(error);
          this.error = 'Failed to load linked products. Please try again.';
          return of(null);
        }),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe();
  }

  private initializeSelectedItems(): void {
    const itemsArray = this.form.get('selected_items') as FormArray;

    // Clear existing items
    itemsArray.clear();

    // Add form controls for each linked product based on quantity
    const controls = this.linkedProducts.flatMap((linkedProduct) => {
      const quantity = linkedProduct.quantity || 1;

      return Array.from({ length: quantity }, () =>
        this.fb.control(null, [
          Validators.required,
          validateFieldIsUniqueInFormArray(itemsArray)
        ])
      );
    });

    controls.forEach((control) => {
      itemsArray.push(control);
    });
  }

  get selectedItemsControls() {
    return (this.form.get('selected_items') as FormArray).controls;
  }

  initializeAvailableItemsForProducts() {
    this.linkedProducts.forEach((linkedProduct) => {
      this.availableItemsForProducts.set(
        linkedProduct.linkable.id,
        linkedProduct.linkable.items.map((item) => ({
          itemId: item.id,
          itemCode: item.code,
          available: item.status === 'Available',
          deleted: item.deleted === 1,
          hasCurrentWorkshopJobs: item.has_current_workshop_jobs
        }))
      );
    });
  }

  initializeLinkedProductsWithIndices(): void {
    let result: {
      linkedProduct: LinkedProductWithLinkable<Product>;
      index: number;
    }[] = [];

    let indexCounter = 0;
    this.linkedProducts.forEach((linkedProduct) => {
      const quantity = linkedProduct.quantity || 1;
      for (let i = 0; i < quantity; i++) {
        result.push({
          linkedProduct: linkedProduct,
          index: indexCounter++
        });
      }
    });

    this.linkedProductsWithIndices = result;
  }

  onSubmit(): void {
    if (this.form.invalid) {
      return;
    }

    const countOfItemPerProduct = {};
    const mappedSelectedItems = this.linkedProductsWithIndices.reduce(
      (acc, { linkedProduct, index }) => {
        countOfItemPerProduct[linkedProduct.id] =
          (countOfItemPerProduct[linkedProduct.id] || 0) + 1;
        acc[`${linkedProduct.id}-${countOfItemPerProduct[linkedProduct.id]}`] =
          this.selectedItemsControls[index].value;

        return acc;
      },
      {}
    );

    this.loading = true;

    this.hireItemService
      .createVirtualProductHireItems(this.docketId, {
        ...this.form.value,
        selected_items: mappedSelectedItems
      })
      .pipe(
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: (hireItem) => {
          this.dialogRef.close(hireItem);
        },
        error: (error) => {
          this.error = 'Failed to create hire items. Please try again.';
        }
      });
  }

  cancel(): void {
    this.dialogRef.close(false);
  }
}
